// 在线轻编辑器（阶段 3 核心 · 任务 3.2/3.3/3.4/3.5/3.6）
// 布局对齐 UI_design/deck.html 原型：左缩略图轨道 / 中画布 / 右工具栏。
// 数据流：canvas 编辑 → slides state → 800ms 防抖整册保存 → 服务端原子替换；
// 撤销栈按「聚焦会话」合并（同一字段连续输入 = 一步）；冲突 409 → 用户裁决。

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, ApiError, toSavePayload } from "../lib/api";
import { downloadFile } from "../lib/download";
import { newSlide, resolveLayoutV2, setByPath, V2_LAYOUT_NAMES, type Block, type EditorSlide } from "../lib/slides";
import { THEMES } from "../lib/tokens.generated";
import { STYLE_GROUPS, chipColors } from "../lib/theme-ui";
import SlideCanvas from "../components/SlideCanvas";
import ThumbRail from "../components/editor/ThumbRail";
import ImageDialog from "../components/editor/ImageDialog";
import VersionsPanel from "../components/editor/VersionsPanel";
import "./Editor.css";

type SaveState = "idle" | "saving" | "saved" | "error";
type ExportState = null | "rendering" | { url: string; filename: string } | { error: string };

const UNDO_CAP = 30; // ≥20 步（验收口径）

export default function Editor() {
  const { deckId } = useParams();

  // ---- 数据态 ----
  const [deckTitle, setDeckTitle] = useState("");
  const [deckStyle, setDeckStyle] = useState<{ style: string | null; themeId: string | null }>({ style: null, themeId: null });
  const [themeBusy, setThemeBusy] = useState(false);

  /** V2 换肤（020 F7）：落库后本地状态更新 → SlideCanvas/ThumbRail 随 props 重渲染 */
  async function pickTheme(themeId: string) {
    if (!deckId || themeBusy) return;
    setThemeBusy(true);
    try {
      const r = await api.updateDeckTheme(deckId, themeId);
      setDeckStyle({ style: r.style, themeId: r.theme_id });
      toast("已更换视觉主题，导出将自动跟随");
    } catch (e) {
      toast("换主题失败：" + (e as Error).message);
    } finally {
      setThemeBusy(false);
    }
  }
  const [slides, setSlides] = useState<EditorSlide[]>([]);
  const [cur, setCur] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [rev, setRev] = useState(0); // 外部变更计数：undo/redo/restore/load 时 bump，强制画布重挂载

  // ---- 保存态 ----
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [savedAt, setSavedAt] = useState("");
  const [conflict, setConflict] = useState(false);

  // ---- UI 态 ----
  const [imgPick, setImgPick] = useState<number | null>(null); // 正在换图的 block 下标
  const [exportState, setExportState] = useState<ExportState>(null);
  const [toastMsg, setToastMsg] = useState("");
  const [verKey, setVerKey] = useState(0);

  // ---- refs（回调里读最新值，避免闭包过期） ----
  const slidesRef = useRef<EditorSlide[]>([]);
  const curRef = useRef(0);
  const dirtyRef = useRef(false);
  const baseRef = useRef<string | null>(null);      // 冲突检测基准（服务端 max(updated_at)）
  const undoStack = useRef<string[]>([]);
  const redoStack = useRef<string[]>([]);
  const editSession = useRef<string | null>(null);  // 当前聚焦字段 path
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingRef = useRef(false);
  /** 045 #22：上传预览用的 blob URL 登记——离开页面统一 revoke，防每次上传泄漏一个 */
  const blobUrls = useRef<string[]>([]);

  useEffect(() => () => {
    blobUrls.current.forEach((u) => URL.revokeObjectURL(u));
    blobUrls.current = [];
  }, []);

  // 移动端只读（任务 3.6）：窄屏或触屏设备不提供编辑
  const readonly = useMemo(
    () =>
      window.matchMedia("(max-width: 900px)").matches ||
      window.matchMedia("(pointer: coarse)").matches,
    [],
  );

  const toast = useCallback((msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg((m) => (m === msg ? "" : m)), 2400);
  }, []);

  // ---------- 加载 ----------
  const load = useCallback(async () => {
    if (!deckId) return;
    setLoading(true);
    setErr(null);
    try {
      const d = await api.getDeck(deckId);
      setDeckTitle(d.deck.title);
      setDeckStyle({ style: d.deck.style ?? null, themeId: d.deck.theme_id ?? null });
      const mapped: EditorSlide[] = d.slides.map((r) => ({ id: r.id, layout: r.layout, content: r.content }));
      slidesRef.current = mapped;
      setSlides(mapped);
      baseRef.current = d.maxUpdatedAt;
      dirtyRef.current = false;
      undoStack.current = [];
      redoStack.current = [];
      setCur(0);
      curRef.current = 0;
      setConflict(false);
      setSaveState("idle");
      setRev((v) => v + 1);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [deckId]);

  useEffect(() => { void load(); }, [load]);

  // ---------- 保存（800ms 防抖 · 任务 3.2 验收：改动 800ms 内落库） ----------
  /** 045 #23：在途保存的 promise——saveNow 重入时 await 它，防导出/快照基于旧库状态 */
  const savingPromise = useRef<Promise<boolean> | null>(null);
  const saveNow = useCallback(async (): Promise<boolean> => {
    if (!deckId || !dirtyRef.current) return true;
    if (savingRef.current) return savingPromise.current ?? true;
    savingRef.current = true;
    setSaveState("saving");
    const run = (async (): Promise<boolean> => {
      try {
        const res = await api.saveSlides(deckId, toSavePayload(slidesRef.current), baseRef.current);
        baseRef.current = res.serverUpdatedAt ?? baseRef.current;
        dirtyRef.current = false;
        setSaveState("saved");
        setSavedAt(new Date().toLocaleTimeString("zh-CN"));
        return true;
      } catch (e) {
        const ae = e as ApiError;
        if (ae.status === 409) {
          setConflict(true);
          setSaveState("idle");
          return false;
        }
        setSaveState("error");
        setTimeout(() => { if (dirtyRef.current) void saveNow(); }, 3000); // 失败自动重试
        return false;
      } finally {
        savingRef.current = false;
        savingPromise.current = null;
      }
    })();
    savingPromise.current = run;
    return run;
  }, [deckId]);

  const markDirty = useCallback(() => {
    dirtyRef.current = true;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { void saveNow(); }, 800);
  }, [saveNow]);

  // 离开页面前有未保存修改 → 提示（任务 3.6 细节）
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current) e.preventDefault();
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);

  // ---------- 撤销 / 重做（任务 3.4 · ≥20 步） ----------
  const pushUndo = useCallback(() => {
    undoStack.current.push(JSON.stringify(slidesRef.current));
    if (undoStack.current.length > UNDO_CAP) undoStack.current.shift();
    redoStack.current = [];
  }, []);

  const applySnapshot = useCallback((json: string) => {
    const parsed = JSON.parse(json) as EditorSlide[];
    slidesRef.current = parsed;
    setSlides(parsed);
    setCur((c) => {
      const n = Math.min(c, parsed.length - 1);
      curRef.current = n;
      return n;
    });
    setRev((v) => v + 1);
    markDirty();
  }, [markDirty]);

  const undo = useCallback(() => {
    if (!undoStack.current.length) { toast("没有可撤销的操作"); return; }
    redoStack.current.push(JSON.stringify(slidesRef.current));
    applySnapshot(undoStack.current.pop()!);
  }, [applySnapshot, toast]);

  const redo = useCallback(() => {
    if (!redoStack.current.length) { toast("没有可重做的操作"); return; }
    undoStack.current.push(JSON.stringify(slidesRef.current));
    applySnapshot(redoStack.current.pop()!);
  }, [applySnapshot, toast]);

  // ---------- 编辑回调 ----------
  /** 文字输入：同一字段的连续输入合并为一步撤销（聚焦会话） */
  const handleEdit = useCallback((path: string, value: string | number) => {
    if (editSession.current !== path) {
      pushUndo();
      editSession.current = path;
    }
    const i = curRef.current;
    const next = slidesRef.current.map((s, k) =>
      k === i ? { ...s, content: setByPath(s.content, path, value) } : s);
    slidesRef.current = next;
    setSlides(next);
    markDirty();
  }, [pushUndo, markDirty]);

  const handleEndEdit = useCallback(() => { editSession.current = null; }, []);

  /** 离散操作（拖拽/删页/换图…）：立即压一步撤销 */
  const mutate = useCallback((fn: (prev: EditorSlide[]) => EditorSlide[]) => {
    pushUndo();
    const next = fn(slidesRef.current);
    slidesRef.current = next;
    setSlides(next);
    markDirty();
  }, [pushUndo, markDirty]);

  const onSelect = useCallback((i: number) => { curRef.current = i; setCur(i); }, []);

  const onMove = useCallback((from: number, to: number) => {
    mutate((s) => {
      const n = [...s];
      const [m] = n.splice(from, 1);
      n.splice(to, 0, m);
      return n;
    });
    curRef.current = to;
    setCur(to);
    toast(`已移动第 ${from + 1} 页到第 ${to + 1} 位`);
  }, [mutate, toast]);

  const onDelete = useCallback((i: number) => {
    if (slidesRef.current.length <= 1) { toast("至少保留一页"); return; }
    mutate((s) => s.filter((_, k) => k !== i));
    setCur((c) => {
      const n = Math.max(0, c - (i <= c ? 1 : 0));
      curRef.current = n;
      return n;
    });
    toast("已删除该页（可撤销）");
  }, [mutate, toast]);

  const onAdd = useCallback(() => {
    mutate((s) => [...s, newSlide()]);
    const n = slidesRef.current.length - 1;
    curRef.current = n;
    setCur(n);
    toast("已在末尾新增一页");
  }, [mutate, toast]);

  const onImagePick = useCallback((picked: { assetId?: string; path: string; url: string }) => {
    if (imgPick === null) return;
    const bi = imgPick;
    const old = slidesRef.current[curRef.current]?.content.blocks[bi];
    const prompt = old?.type === "image" ? old.prompt : undefined;
    // 045 #22：旧图若是 blob URL 当场 revoke（已被新图替换，不再引用）；新 blob 登记到卸载时释放
    const oldUrl = old && old.type === "image" ? old.url : undefined;
    if (oldUrl?.startsWith("blob:")) URL.revokeObjectURL(oldUrl);
    if (picked.url.startsWith("blob:")) blobUrls.current.push(picked.url);
    const nb: Block = { type: "image", path: picked.path, url: picked.url, ...(picked.assetId ? { assetId: picked.assetId } : {}), ...(prompt ? { prompt } : {}) };
    mutate((s) => s.map((sl, k) =>
      k === curRef.current ? { ...sl, content: setByPath(sl.content, `blocks.${bi}`, nb) } : sl));
    setImgPick(null);
    toast("配图已替换，导出时将嵌入新图");
  }, [imgPick, mutate, toast]);

  // ---------- 快捷键（Ctrl+Z / Ctrl+Y / Ctrl+S） ----------
  useEffect(() => {
    if (readonly) return;
    const h = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if (k === "y" || (k === "z" && e.shiftKey)) { e.preventDefault(); redo(); }
      else if (k === "s") { e.preventDefault(); void saveNow(); }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [undo, redo, saveNow, readonly]);

  // ---------- 导出（任务 3.5：编辑后一键再导出，先落库再渲染） ----------
  const doExport = useCallback(async (versionId?: string) => {
    if (!deckId) return;
    setExportState("rendering");
    if (!versionId) {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      const ok = await saveNow();
      if (!ok) {
        setExportState({ error: "有未保存的修改未能落库（冲突或网络错误），请先处理" });
        return;
      }
    }
    try {
      const { url, filename } = await api.exportDeck(deckId, versionId);
      setExportState({ url, filename });
    } catch (e) {
      setExportState({ error: (e as Error).message });
    }
  }, [deckId, saveNow]);

  // 弹层「下载」按钮：blob 方式下载并以干净中文名命名（绕开畸形 Content-Disposition）
  const doDownload = useCallback(async (url: string, filename: string) => {
    try {
      await downloadFile(url, filename);
      toast(`已开始下载：${filename}`);
    } catch (e) {
      toast(`❌ ${(e as Error).message}`);
    }
  }, [toast]);

  // ---------- 版本快照（任务 3.5） ----------
  const doSnapshot = useCallback(async () => {
    if (!deckId) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    const ok = await saveNow(); // 快照冷冻的是库中状态，先确保落库
    if (!ok) throw new Error("保存未完成，无法快照");
    await api.createVersion(deckId, `手动快照 ${new Date().toLocaleString("zh-CN")}`);
    setVerKey((k) => k + 1);
  }, [deckId, saveNow]);

  const doRestore = useCallback(async (versionId: string, version: number) => {
    if (!deckId) return;
    if (!window.confirm(`回滚到 v${version}？\n当前状态会先自动保存为一个新快照，之后还能再回滚回来。`)) return;
    await api.restoreVersion(deckId, versionId);
    setVerKey((k) => k + 1);
    await load();
    toast(`已回滚到 v${version}（回滚前的状态已自动快照）`);
  }, [deckId, load, toast]);

  // ---------- 渲染 ----------
  const slide = slides[cur];

  const saveHint = useMemo(() => {
    if (conflict) return "⚠ 检测到冲突，请先处理";
    switch (saveState) {
      case "saving": return "正在保存…";
      case "saved": return `已自动保存 ${savedAt}`;
      case "error": return "保存失败，3 秒后自动重试…";
      default: return "所有修改自动保存 · 缩略图可拖拽排序 · 点击配图位换图";
    }
  }, [saveState, savedAt, conflict]);

  if (loading) {
    return (
      <div className="aim-editor flex min-h-screen items-center justify-center">
        <p className="text-muted">编辑器加载中…</p>
      </div>
    );
  }
  if (err) {
    return (
      <div className="aim-editor flex min-h-screen flex-col items-center justify-center gap-3">
        <p className="text-red-600">{err}</p>
        <Link to={`/deck/${deckId}`} className="btn-ghost">← 返回结果页</Link>
      </div>
    );
  }

  return (
    <div className="aim-editor flex h-screen flex-col">
      {/* 顶栏 */}
      <header className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-2">
        <Link to={`/deck/${deckId}`} className="btn-ghost !px-2.5 !py-1 text-xs">← 结果页</Link>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-bold text-ink">✏️ {deckTitle}</div>
          <div className="text-xs text-muted">第 {cur + 1} / {slides.length} 页 · {saveHint}</div>
        </div>
        {!readonly && (
          <>
            <button className="btn-ghost !px-2.5 !py-1 text-xs" onClick={undo} title="Ctrl+Z">↩ 撤销</button>
            <button className="btn-ghost !px-2.5 !py-1 text-xs" onClick={redo} title="Ctrl+Y">↪ 重做</button>
          </>
        )}
        <button
          className="btn-primary !px-3 !py-1 text-xs"
          onClick={() => void doExport()}
          disabled={exportState === "rendering"}
        >
          {exportState === "rendering" ? "渲染中…" : "导出 PPTX"}
        </button>
      </header>

      {/* 冲突横幅（任务 3.6：他窗口/设备改过 → 用户裁决） */}
      {conflict && (
        <div className="aim-banner-warn flex items-center gap-3 border-b px-4 py-2 text-sm">
          <span className="flex-1">⚠ 检测到这份方案在其他窗口/设备有更新的修改，为避免互相覆盖已暂停自动保存。</span>
          <button className="btn-ghost !px-2.5 !py-1 text-xs" onClick={() => void load()}>加载最新</button>
          <button
            className="btn-primary !px-2.5 !py-1 text-xs"
            onClick={() => {
              baseRef.current = null; // 跳过冲突检测，强制覆盖
              setConflict(false);
              dirtyRef.current = true;
              void saveNow();
            }}
          >
            以我的修改覆盖
          </button>
        </div>
      )}

      {/* 移动端只读提示（任务 3.6） */}
      {readonly && (
        <div className="aim-banner-info border-b px-4 py-2 text-sm">
          📱 移动端为只读预览，请在桌面浏览器中编辑。
        </div>
      )}

      {/* 三栏 */}
      <div className="flex min-h-0 flex-1">
        {/* 左：缩略图轨道 */}
        <aside className="w-44 flex-none border-r border-slate-200 bg-white sm:w-52">
          <ThumbRail
            slides={slides} cur={cur} readonly={readonly}
            onSelect={onSelect} onMove={onMove} onDelete={onDelete} onAdd={onAdd}
            style={deckStyle.style} themeId={deckStyle.themeId}
          />
        </aside>

        {/* 中：画布 */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-8">
          <div className="mx-auto max-w-5xl">
            {slide ? (
              <SlideCanvas
                key={`${cur}:${rev}`}
                slide={slide.content}
                pageIdx={cur + 1}
                editable={!readonly}
                onEdit={handleEdit}
                onBeginEdit={() => { /* 聚焦会话在首次输入时压栈 */ }}
                onEndEdit={handleEndEdit}
                onImageClick={(bi) => setImgPick(bi)}
                style={deckStyle.style}
                themeId={deckStyle.themeId}
                totalPages={slides.length}
              />
            ) : (
              <p className="py-20 text-center text-muted">没有页面了</p>
            )}
            <p className="mt-3 text-center text-xs text-muted">{saveHint}</p>
          </div>
        </main>

        {/* 右：工具栏（桌面端） */}
        {!readonly && (
          <aside className="hidden w-64 flex-none space-y-3 overflow-y-auto border-l border-slate-200 bg-slate-50 p-3 lg:block">
            {/* 当前页版式信息（020 PRD §5.3）：展示该页的排版形态 */}
            {slide && deckStyle.style && deckStyle.style !== "legacy" && (() => {
              const lv2 = resolveLayoutV2(slide.content);
              return (
                <div className="card !p-4">
                  <h4 className="mb-1.5 text-sm font-bold text-ink">📐 当前版式</h4>
                  <p className="text-sm font-medium text-ink">{V2_LAYOUT_NAMES[lv2] ?? lv2}</p>
                  <p className="mt-0.5 font-mono text-[11px] text-muted">{lv2}</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-muted">
                    版式决定本页的排版骨架，内容由 AI 按此形态撰写；换版式请整册重新生成。
                  </p>
                </div>
              );
            })()}
            <div className="card !p-4">
              <h4 className="mb-1.5 text-sm font-bold text-ink">🔧 编辑提示</h4>
              <p className="text-xs leading-relaxed text-muted">
                点击画布中的<b>任意文字</b>直接修改；点击<b>配图位</b>上传或从素材库换图；
                左侧缩略图可<b>拖拽排序</b>、删页。Ctrl+Z 撤销 · Ctrl+Y 重做 · Ctrl+S 立即保存。
              </p>
            </div>
            <div className="card !p-4">
              <h4 className="mb-1.5 text-sm font-bold text-ink">📝 演讲备注</h4>
              <textarea
                className="input !text-xs" rows={4}
                placeholder="为本页添加演讲备注…"
                value={slide?.content.notes ?? ""}
                onChange={(e) => handleEdit("notes", e.target.value)}
                onBlur={handleEndEdit}
              />
            </div>
            {/* V2 视觉主题（020 F7）：整册换肤，导出自动跟随 */}
            {deckStyle.style && deckStyle.style !== "legacy" && (
              <div className="card !p-4">
                <h4 className="mb-1.5 text-sm font-bold text-ink">🎨 视觉主题</h4>
                <p className="mb-2 text-xs text-muted">整册即时换肤，导出 pptx 自动跟随。</p>
                <div className="space-y-2">
                  {STYLE_GROUPS.map((g) => (
                    <div key={g.id}>
                      <div className="mb-1 text-[11px] font-medium text-secondary">{g.label}</div>
                      <div className="grid grid-cols-2 gap-1.5">
                        {Object.values(THEMES).filter((t) => t.style === g.id).map((t) => {
                          const active = deckStyle.themeId === t.id;
                          // 双色 chip 取色见 lib/theme-ui.ts（accent 系 + 底纸系）
                          const [c1, c2] = chipColors(t);
                          return (
                            <button
                              key={t.id}
                              type="button"
                              disabled={themeBusy}
                              onClick={() => pickTheme(t.id)}
                              title={t.suitable}
                              className={`flex items-center gap-1.5 rounded-md border px-1.5 py-1 text-left text-[11px] transition ${
                                active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-slate-200 hover:border-slate-300"
                              } ${themeBusy ? "opacity-50" : ""}`}
                            >
                              <span className="flex h-4 w-4 flex-none overflow-hidden rounded-sm border border-slate-200">
                                <span className="h-full w-1/2" style={{ background: c1 }} />
                                <span className="h-full w-1/2" style={{ background: c2 }} />
                              </span>
                              <span className="truncate">{t.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <VersionsPanel
              deckId={deckId!}
              refreshKey={verKey}
              onSnapshot={doSnapshot}
              onRestore={doRestore}
              onExportVersion={async (vid) => { await doExport(vid); }}
              onToast={toast}
            />
          </aside>
        )}
      </div>

      {/* 换配图对话框 */}
      {imgPick !== null && deckId && (
        <ImageDialog deckId={deckId} onClose={() => setImgPick(null)} onPick={onImagePick} />
      )}

      {/* 导出弹层 */}
      {exportState !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setExportState(null)}
        >
          <div className="card aim-dialog-card w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-2 text-base font-bold text-ink">📤 导出 .pptx</h3>
            {exportState === "rendering" && (
              <p className="text-sm text-muted">正在用最新 slides 渲染 PPT（pptxgenjs · Edge Function）…</p>
            )}
            {typeof exportState === "object" && "url" in exportState && (
              <>
                <p className="text-sm font-medium text-green-700">✅ 渲染完成，将以「{exportState.filename}」保存。</p>
                <button
                  className="btn-primary mt-3 w-full"
                  onClick={() => doDownload(exportState.url, exportState.filename)}
                >
                  ⬇ 下载 .pptx
                </button>
              </>
            )}
            {typeof exportState === "object" && "error" in exportState && (
              <p className="text-sm text-red-600">❌ {exportState.error}</p>
            )}
            <div className="mt-4 text-right">
              <button className="btn-ghost !py-1 text-xs" onClick={() => setExportState(null)}>关闭</button>
            </div>
          </div>
        </div>
      )}

      {/* toast */}
      {toastMsg && (
        <div className="aim-toast fixed bottom-6 left-1/2 z-50 -translate-x-1/2 px-4 py-2 text-sm">
          {toastMsg}
        </div>
      )}
    </div>
  );
}
